import type { Person } from "../types";
import type {
  BookingConfidence,
  BookingProjectCoverageSummary,
  BookingReconciliationReport,
  BookingReconciliationRow,
  BookingReconciliationStatus,
  BookingReconciliationSummary,
  BookingRecord,
  FinanceAlert,
  FinanceAlertReasonCode,
  FinanceAlertSeverity,
  TimeLog,
} from "../models/production";

type ProjectLike = {
  id: string;
  setup?: { projectName?: string };
  financials?: { totalCost?: number };
  profitability?: { bidValue?: number };
};

interface BuildBookingReconciliationReportArgs {
  bookings: BookingRecord[];
  timeLogs: TimeLog[];
  people: Array<Pick<Person, "id" | "displayName" | "fullName" | "name" | "department" | "dailyRate">>;
  projects?: ProjectLike[];
  startDate: string;
  endDate: string;
  now?: string;
}

function toIsoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function startOfIsoWeek(value: string | Date) {
  const date = new Date(value);
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() - day + 1);
  return utc;
}

function addUtcDays(date: Date, days: number) {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function countWorkingDaysBetween(startIso?: string, endIso?: string) {
  if (!startIso || !endIso) return 0;
  const start = new Date(`${startIso}T00:00:00.000Z`);
  const end = new Date(`${endIso}T00:00:00.000Z`);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end < start) return 0;
  let total = 0;
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    const weekday = cursor.getUTCDay() || 7;
    if (weekday >= 1 && weekday <= 5) total += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return total;
}

function clampRangeToWeek(startIso?: string, endIso?: string, weekStartIso?: string) {
  if (!startIso || !endIso || !weekStartIso) return { overlapDays: 0, weekEndIso: weekStartIso || "" };
  const weekStart = startOfIsoWeek(weekStartIso);
  const weekEnd = addUtcDays(weekStart, 6);
  const overlapStart = new Date(Math.max(new Date(`${startIso}T00:00:00.000Z`).getTime(), weekStart.getTime()));
  const overlapEnd = new Date(Math.min(new Date(`${endIso}T00:00:00.000Z`).getTime(), weekEnd.getTime()));
  if (overlapEnd < overlapStart) {
    return { overlapDays: 0, weekEndIso: toIsoDate(weekEnd) };
  }
  return {
    overlapDays: countWorkingDaysBetween(toIsoDate(overlapStart), toIsoDate(overlapEnd)),
    weekEndIso: toIsoDate(weekEnd),
  };
}

function listIsoWeeksBetween(startDate: string, endDate: string) {
  const start = startOfIsoWeek(startDate);
  const end = startOfIsoWeek(endDate);
  const weeks: string[] = [];
  let cursor = new Date(start.getTime());
  while (cursor <= end) {
    weeks.push(toIsoDate(cursor));
    cursor = addUtcDays(cursor, 7);
  }
  return weeks;
}

function roundDays(value: number) {
  return Math.round(value * 100) / 100;
}

function resolvePersonName(person?: Pick<Person, "displayName" | "fullName" | "name" | "id">, fallback?: string) {
  return person?.displayName || person?.fullName || person?.name || fallback || "Unknown artist";
}

function getLoggedCost(timeLog: TimeLog, person?: Pick<Person, "dailyRate">) {
  if (typeof timeLog.cost === "number" && Number.isFinite(timeLog.cost)) return timeLog.cost;
  if (typeof person?.dailyRate === "number" && Number.isFinite(person.dailyRate)) {
    return Math.round(((timeLog.minutes || 0) / 480) * person.dailyRate * 100) / 100;
  }
  return 0;
}

function getConfidence(bookedMinutes: number, loggedMinutes: number, bookingConfidenceValues: BookingConfidence[]): BookingConfidence {
  if (bookingConfidenceValues.includes("low")) return "low";
  if (bookingConfidenceValues.includes("medium")) return "medium";
  if (bookedMinutes === 0 && loggedMinutes === 0) return "low";
  return "high";
}

function getStatus(bookedMinutes: number, loggedMinutes: number): BookingReconciliationStatus {
  if (bookedMinutes <= 0 && loggedMinutes <= 0) return "healthy";
  if (bookedMinutes > 0 && loggedMinutes <= 0) return "booked_without_logs";
  if (loggedMinutes > 0 && bookedMinutes <= 0) return "logged_without_booking";
  if (Math.max(bookedMinutes - loggedMinutes, 0) === 0) return "booked_matches_logged";
  if (bookedMinutes > loggedMinutes) return "partially_logged_but_booked";
  return "mixed_review_needed";
}

function makeAlert(args: {
  now: string;
  dedupeKey: string;
  severity: FinanceAlertSeverity;
  reasonCode: FinanceAlertReasonCode;
  title: string;
  summary: string;
  projectId?: string;
  personId?: string;
  weekStart?: string;
  projectName?: string;
  artistName?: string;
}): FinanceAlert {
  return {
    id: `finance_alert:${args.dedupeKey}`,
    dedupeKey: args.dedupeKey,
    severity: args.severity,
    reasonCode: args.reasonCode,
    title: args.title,
    summary: args.summary,
    projectId: args.projectId,
    personId: args.personId,
    weekStart: args.weekStart,
    relatedEntities: [
      ...(args.projectId
        ? [{ entityType: "project" as const, entityId: args.projectId, label: args.projectName }]
        : []),
      ...(args.personId
        ? [{ entityType: "artist" as const, entityId: args.personId, parentEntityType: "project" as const, parentEntityId: args.projectId, label: args.artistName }]
        : []),
    ],
    alertOpen: true,
    createdAt: args.now,
    updatedAt: args.now,
  };
}

export function buildBookingReconciliationReport(args: BuildBookingReconciliationReportArgs): BookingReconciliationReport {
  const now = args.now || new Date().toISOString();
  const peopleById = new Map(args.people.map((person) => [person.id, person]));
  const projectsById = new Map((args.projects || []).map((project) => [project.id, project]));
  const weeks = listIsoWeeksBetween(args.startDate, args.endDate);
  const rowMap = new Map<string, BookingReconciliationRow & { _bookingConfidences: BookingConfidence[] }>();

  const ensureRow = (params: {
    artistId?: string;
    artistName: string;
    projectId?: string;
    projectName: string;
    weekStart: string;
    weekEnd: string;
    department?: string;
  }) => {
    const key = [params.artistId || "unknown", params.projectId || "unknown", params.weekStart].join(":");
    if (!rowMap.has(key)) {
      rowMap.set(key, {
        id: key,
        artist_id: params.artistId,
        artist_name: params.artistName,
        project_id: params.projectId,
        project_name: params.projectName,
        week_start: params.weekStart,
        week_end: params.weekEnd,
        department: params.department,
        booked_project_minutes: 0,
        booked_project_days: 0,
        logged_project_minutes: 0,
        logged_project_days: 0,
        unlogged_booked_minutes: 0,
        unlogged_booked_days: 0,
        booked_labour_cost: 0,
        logged_labour_cost: 0,
        recovered_project_cost_from_bookings: 0,
        booking_vs_log_confidence: "low",
        reconciliation_status: "healthy",
        booking_count: 0,
        log_count: 0,
        reason_codes: [],
        notes: [],
        _bookingConfidences: [],
      });
    }
    return rowMap.get(key)!;
  };

  for (const booking of args.bookings.filter((entry) => !entry.isDeleted)) {
    const effectiveStart = booking.startDate && booking.startDate > args.startDate ? booking.startDate : args.startDate;
    const effectiveEnd = booking.endDate && booking.endDate < args.endDate ? booking.endDate : args.endDate;
    if (!effectiveStart || !effectiveEnd || effectiveEnd < effectiveStart) continue;

    const totalWorkingDays = booking.bookedWorkingDays || countWorkingDaysBetween(booking.startDate, booking.endDate) || 0;
    const totalBookedMinutes = booking.bookedMinutes || Math.round(totalWorkingDays * 480);
    const totalBookedCost = booking.bookedLabourCost || 0;
    const person = booking.personId ? peopleById.get(booking.personId) : undefined;
    const artistName = resolvePersonName(person, booking.personName);
    const projectName =
      projectsById.get(String(booking.projectId || ""))?.setup?.projectName ||
      booking.fptProjectName ||
      "Unknown project";

    for (const weekStart of weeks) {
      const { overlapDays, weekEndIso } = clampRangeToWeek(effectiveStart, effectiveEnd, weekStart);
      if (overlapDays <= 0) continue;
      const ratio = totalWorkingDays > 0 ? overlapDays / totalWorkingDays : 0;
      const row = ensureRow({
        artistId: booking.personId,
        artistName,
        projectId: booking.projectId,
        projectName,
        weekStart,
        weekEnd: weekEndIso,
        department: booking.department || person?.department,
      });
      row.booked_project_days += overlapDays;
      row.booked_project_minutes += Math.round(totalBookedMinutes * ratio);
      row.booked_labour_cost += Math.round(totalBookedCost * ratio * 100) / 100;
      row.booking_count += 1;
      row._bookingConfidences.push(booking.bookingConfidence || "low");
    }
  }

  for (const timeLog of args.timeLogs.filter((entry) => entry.projectId)) {
    const weekStart = toIsoDate(startOfIsoWeek(timeLog.logDate));
    if (weekStart < weeks[0] || weekStart > weeks[weeks.length - 1]) continue;
    const weekEnd = toIsoDate(addUtcDays(startOfIsoWeek(weekStart), 6));
    const person = timeLog.personId ? peopleById.get(timeLog.personId) : undefined;
    const project = projectsById.get(String(timeLog.projectId || ""));
    const row = ensureRow({
      artistId: timeLog.personId,
      artistName: resolvePersonName(person, timeLog.fptUserId || timeLog.personId),
      projectId: timeLog.projectId,
      projectName: project?.setup?.projectName || "Unknown project",
      weekStart,
      weekEnd,
      department: person?.department,
    });
    row.logged_project_minutes += timeLog.minutes || 0;
    row.logged_labour_cost += getLoggedCost(timeLog, person);
    row.log_count += 1;
  }

  const rows = Array.from(rowMap.values())
    .map((row) => {
      row.booked_project_days = roundDays(row.booked_project_minutes / 480);
      row.logged_project_days = roundDays(row.logged_project_minutes / 480);
      row.unlogged_booked_minutes = Math.max(row.booked_project_minutes - row.logged_project_minutes, 0);
      row.unlogged_booked_days = roundDays(row.unlogged_booked_minutes / 480);
      row.recovered_project_cost_from_bookings = Math.max(
        Math.round((row.booked_labour_cost - row.logged_labour_cost) * 100) / 100,
        0
      );
      row.booking_vs_log_confidence = getConfidence(
        row.booked_project_minutes,
        row.logged_project_minutes,
        row._bookingConfidences
      );
      row.reconciliation_status = getStatus(row.booked_project_minutes, row.logged_project_minutes);
      row.reason_codes = Array.from(
        new Set(
          [
            row.reconciliation_status === "booked_without_logs" ? "booked_without_logs" : "",
            row.reconciliation_status === "logged_without_booking" ? "logged_without_booking" : "",
            row.reconciliation_status === "partially_logged_but_booked" ? "partially_logged_but_booked" : "",
            row.recovered_project_cost_from_bookings > 0 ? "recovered_booking_cost" : "",
          ].filter(Boolean)
        )
      );
      row.notes = [
        row.recovered_project_cost_from_bookings > 0
          ? `Recovered ${roundDays(row.unlogged_booked_minutes / 480)} unlogged booked days into project cost.`
          : row.reconciliation_status === "booked_matches_logged"
            ? "Booked time and logged time broadly align for this week."
            : row.reconciliation_status === "logged_without_booking"
              ? "Task logs exist without matching booking coverage."
              : "",
      ].filter(Boolean);
      delete (row as any)._bookingConfidences;
      return row;
    })
    .filter((row) => row.booked_project_minutes > 0 || row.logged_project_minutes > 0)
    .sort((a, b) =>
      String(a.project_name || "").localeCompare(String(b.project_name || "")) ||
      String(a.artist_name || "").localeCompare(String(b.artist_name || "")) ||
      String(a.week_start || "").localeCompare(String(b.week_start || ""))
    );

  const summary: BookingReconciliationSummary = rows.reduce(
    (acc, row) => {
      acc.total_booked_minutes += row.booked_project_minutes;
      acc.total_logged_minutes += row.logged_project_minutes;
      acc.total_unlogged_booked_minutes += row.unlogged_booked_minutes;
      acc.total_booked_labour_cost += row.booked_labour_cost;
      acc.total_logged_labour_cost += row.logged_labour_cost;
      acc.total_recovered_project_cost_from_bookings += row.recovered_project_cost_from_bookings;
      acc.row_count += 1;
      return acc;
    },
    {
      total_booked_minutes: 0,
      total_booked_days: 0,
      total_logged_minutes: 0,
      total_logged_days: 0,
      total_unlogged_booked_minutes: 0,
      total_unlogged_booked_days: 0,
      total_booked_labour_cost: 0,
      total_logged_labour_cost: 0,
      total_recovered_project_cost_from_bookings: 0,
      booking_adjusted_total_labour_cost: 0,
      booking_data_coverage_percent: 0,
      timelog_coverage_vs_bookings_percent: 0,
      row_count: 0,
    }
  );

  summary.total_booked_days = roundDays(summary.total_booked_minutes / 480);
  summary.total_logged_days = roundDays(summary.total_logged_minutes / 480);
  summary.total_unlogged_booked_days = roundDays(summary.total_unlogged_booked_minutes / 480);
  summary.booking_adjusted_total_labour_cost =
    Math.round((summary.total_logged_labour_cost + summary.total_recovered_project_cost_from_bookings) * 100) / 100;
  summary.booking_data_coverage_percent = summary.total_booked_minutes > 0 ? Math.min((summary.total_logged_minutes / summary.total_booked_minutes) * 100, 100) : 0;
  summary.timelog_coverage_vs_bookings_percent = summary.booking_data_coverage_percent;

  const projectSummaries = rows.reduce<Record<string, BookingProjectCoverageSummary>>((acc, row) => {
    const projectId = row.project_id || "unlinked";
    const project = row.project_id ? projectsById.get(projectId) : undefined;
    const entry =
      acc[projectId] ||
      {
        projectId,
        projectName: row.project_name,
        projectLoggedLabourCost: 0,
        projectBookedLabourCost: 0,
        projectUnloggedBookedLabourCost: 0,
        projectTotalLabourCostIncludingBookings: 0,
        bookedLabourCost: 0,
        loggedLabourCost: 0,
        unloggedBookedLabourCost: 0,
        totalLabourCostIncludingBookings: 0,
        bookingDataCoveragePercent: 0,
        timelogCoverageVsBookingsPercent: 0,
      };
    entry.projectLoggedLabourCost += row.logged_labour_cost;
    entry.projectBookedLabourCost += row.booked_labour_cost;
    entry.projectUnloggedBookedLabourCost += row.recovered_project_cost_from_bookings;
    entry.bookedLabourCost = entry.projectBookedLabourCost;
    entry.loggedLabourCost = entry.projectLoggedLabourCost;
    entry.unloggedBookedLabourCost = entry.projectUnloggedBookedLabourCost;
    entry.projectTotalLabourCostIncludingBookings =
      Math.round(
        ((project?.financials?.totalCost || entry.projectLoggedLabourCost) + entry.projectUnloggedBookedLabourCost) * 100
      ) / 100;
    entry.totalLabourCostIncludingBookings = entry.projectTotalLabourCostIncludingBookings;
    const bidValue = project?.profitability?.bidValue;
    if (typeof bidValue === "number" && Number.isFinite(bidValue)) {
      entry.bookingAdjustedGrossMargin =
        Math.round((bidValue - entry.projectTotalLabourCostIncludingBookings) * 100) / 100;
      entry.bookingAdjustedMarginPercent =
        bidValue > 0 ? Math.round((entry.bookingAdjustedGrossMargin / bidValue) * 10000) / 100 : undefined;
    }
    acc[projectId] = entry;
    return acc;
  }, {});

  for (const entry of Object.values(projectSummaries)) {
    entry.bookingDataCoveragePercent = entry.projectBookedLabourCost > 0
      ? Math.min((entry.projectLoggedLabourCost / entry.projectBookedLabourCost) * 100, 100)
      : 0;
    entry.timelogCoverageVsBookingsPercent = entry.bookingDataCoveragePercent;
  }

  const alerts: FinanceAlert[] = [];
  for (const row of rows) {
    if (row.reconciliation_status === "booked_without_logs") {
      alerts.push(
        makeAlert({
          now,
          dedupeKey: `project_booked_without_logs:${row.project_id || "unlinked"}:${row.artist_id || "unknown"}:${row.week_start}`,
          severity: "warning",
          reasonCode: "project_booked_without_logs",
          title: "Booked time without logs",
          summary: `${row.artist_name} had booked project time on ${row.project_name} during the week of ${row.week_start} with no matching task logs.`,
          projectId: row.project_id,
          personId: row.artist_id,
          weekStart: row.week_start,
          projectName: row.project_name,
          artistName: row.artist_name,
        })
      );
    }
    if (row.recovered_project_cost_from_bookings >= 500) {
      alerts.push(
        makeAlert({
          now,
          dedupeKey: `artist_project_week_booking_gap:${row.project_id || "unlinked"}:${row.artist_id || "unknown"}:${row.week_start}`,
          severity: row.recovered_project_cost_from_bookings >= 1500 ? "critical" : "warning",
          reasonCode: "artist_project_week_booking_gap",
          title: "Booked vs logged labour gap",
          summary: `${row.project_name} recovered ${row.recovered_project_cost_from_bookings.toFixed(2)} of labour cost from Bookings for ${row.artist_name} in the week of ${row.week_start}.`,
          projectId: row.project_id,
          personId: row.artist_id,
          weekStart: row.week_start,
          projectName: row.project_name,
          artistName: row.artist_name,
        })
      );
    }
  }

  for (const projectSummary of Object.values(projectSummaries)) {
    if (projectSummary.projectUnloggedBookedLabourCost >= 1000) {
      alerts.push(
        makeAlert({
          now,
          dedupeKey: `project_high_recovered_booking_cost:${projectSummary.projectId}`,
          severity: projectSummary.projectUnloggedBookedLabourCost >= 5000 ? "critical" : "warning",
          reasonCode: "project_high_recovered_booking_cost",
          title: "High recovered labour cost from bookings",
          summary: `${projectSummary.projectName} is carrying ${projectSummary.projectUnloggedBookedLabourCost.toFixed(2)} of unlogged booked labour cost.`,
          projectId: projectSummary.projectId,
          projectName: projectSummary.projectName,
        })
      );
    }
    if (projectSummary.timelogCoverageVsBookingsPercent > 0 && projectSummary.timelogCoverageVsBookingsPercent < 70) {
      alerts.push(
        makeAlert({
          now,
          dedupeKey: `project_low_timelog_coverage_vs_bookings:${projectSummary.projectId}`,
          severity: projectSummary.timelogCoverageVsBookingsPercent < 40 ? "critical" : "warning",
          reasonCode: "project_low_timelog_coverage_vs_bookings",
          title: "Low timelog coverage vs bookings",
          summary: `${projectSummary.projectName} only has ${projectSummary.timelogCoverageVsBookingsPercent.toFixed(1)}% timelog coverage against booked labour.`,
          projectId: projectSummary.projectId,
          projectName: projectSummary.projectName,
        })
      );
    }
  }

  return {
    rows,
    summary,
    projectSummaries,
    alerts,
    generatedAt: now,
  };
}
